import { useEffect, useRef } from "react";
import { aiJobOpen } from "@speisekarte/core";
import type { AiJob, AiJobKind } from "@speisekarte/core";
import { ApiError, api } from "./store.ts";

/**
 * A job for the model (S45) followed while the page is open: asked for every two seconds until it is
 * done or failed, then collected (gone on the server) and handed to `settled`. Left and come back
 * (the phone was away), the page finds it again through its GET and goes on from there.
 */
export function useAiJob<K extends AiJobKind>(job: AiJob<K> | null, setJob: (job: AiJob<K> | null) => void, settled: (job: AiJob<K>) => void) {
  const latest = useRef(settled);
  latest.current = settled;
  useEffect(() => {
    if (!job) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!aiJobOpen(job)) {
      void api(`/api/jobs/${job.id}`, { method: "DELETE" })
        .catch(() => undefined)
        .then(() => {
          if (!live) return;
          setJob(null);
          latest.current(job);
        });
      return () => {
        live = false;
      };
    }
    const ask = async () => {
      try {
        const now = (await api<{ job: AiJob<K> }>(`/api/jobs/${job.id}`)).job;
        if (!live) return;
        setJob(now);
        if (aiJobOpen(now)) timer = setTimeout(() => void ask(), 2000);
      } catch (err) {
        if (!live) return;
        if (err instanceof ApiError && err.status === 404) setJob(null);
        // Without a connection: once more a little later.
        else timer = setTimeout(() => void ask(), 5000);
      }
    };
    timer = setTimeout(() => void ask(), 500);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // The job's id and state decide; its place in the queue changing only shows.
  }, [job?.id, job?.status]);
}

/** „Noch 2 Aufträge vor dir.“ while it waits; nothing when it is next or being read. */
export const aheadText = (job: AiJob | null) => (job && job.ahead > 0 ? `Noch ${job.ahead === 1 ? "ein Auftrag" : `${job.ahead} Aufträge`} vor dir.` : "");
